import React, { useRef, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'

interface AssessmentModalRatingScaleProps {
  ratingOptions: number[]
  selectedRating?: string
  onSelect: (rating: string) => void
  onAutoAdvance?: (rating: string) => void
}

export const AssessmentModalRatingScale: React.FC<AssessmentModalRatingScaleProps> = ({
  ratingOptions,
  selectedRating,
  onSelect,
  onAutoAdvance
}) => {
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (pendingRef.current) clearTimeout(pendingRef.current)
    }
  }, [])

  const handleRatingClick = (ratingValue: string) => {
    onSelect(ratingValue)

    if (onAutoAdvance) {
      if (pendingRef.current) clearTimeout(pendingRef.current)
      pendingRef.current = setTimeout(() => {
        onAutoAdvance(ratingValue)
      }, 400)
    }
  }

  return (
    <Box>
      {/* Rating buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 0.75, sm: 1 }
        }}
      >
        {ratingOptions.map((rating) => {
          const ratingValue = rating.toString()
          const isSelected = selectedRating === ratingValue

          return (
            <Box
              key={ratingValue}
              onClick={() => handleRatingClick(ratingValue)}
              sx={{
                flex: 1,
                minHeight: { xs: '52px', sm: '64px' },
                maxWidth: '72px',
                borderRadius: '8px',
                backgroundColor: isSelected ? INTERAX_COLORS.sun10 : 'rgba(255,255,255,0.04)',
                border: isSelected ? `2px solid ${INTERAX_COLORS.sun}` : `1px solid rgba(255,255,255,0.12)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: isSelected ? INTERAX_COLORS.sun20 : 'rgba(255,255,255,0.08)',
                  border: `2px solid ${isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey}`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.headline6,
                  color: isSelected ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
                  fontWeight: 700
                }}
              >
                {rating}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
